import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { SocialBar } from "@/components/SocialBar";
import { BrandPatternLayer } from "@/components/BrandPatternLayer";
import { site, hotlines, openingHours, mapsUrl } from "@/lib/siteConfig";
import { pageContent } from "@/cms/cmsModel";
import { useCmsDocument } from "@/cms/CmsContentProvider";

export default function Kontakt() {
  useCmsDocument();
  const copy = pageContent.contact;
  return (
    <div data-testid="contact-page">
      {/* Hero – full-bleed image with a neutral scrim for legibility */}
      <section className="relative overflow-hidden bg-[#0e1216] text-[#F1F4F8]">
        <img
          src={copy.hero.image}
          alt=""
          aria-hidden="true"
          loading="eager"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, rgba(12,16,20,0.82) 0%, rgba(12,16,20,0.62) 32%, rgba(12,16,20,0.28) 60%, rgba(12,16,20,0.05) 100%)",
          }}
        />
        <div className="relative az-container py-16 lg:py-20">
          <Reveal immediate>
            <span className="az-eyebrow" style={{ color: "#C99A3D" }}>
              <span className="inline-block h-px w-6 bg-current" /> {copy.hero.eyebrow}
            </span>
          </Reveal>
          <Reveal immediate delay={0.05}>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-bold leading-[1.08] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
              {copy.hero.title}
            </h1>
          </Reveal>
          <Reveal immediate delay={0.1}>
            <p className="mt-5 max-w-2xl text-lg text-[rgba(244,246,249,0.82)]">
              {copy.hero.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Content */}
      <section className="relative isolate overflow-hidden bg-[#F1F4F8]/90 py-16 lg:py-24">
        <BrandPatternLayer variant="contact" composition="b" />
        <div className="relative z-10 az-container">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
            {/* Contact details */}
            <div className="lg:col-span-5">
              <h2 className="font-heading text-3xl font-semibold text-[#193D6D]">
                {copy.details.title}
              </h2>
              <p className="mt-3 text-[#586576]">
                {copy.details.subtitle}
              </p>

              <div className="mt-8 space-y-3">
                {hotlines.map((h) => (
                  <a
                    key={h.id}
                    href={`tel:${h.tel}`}
                    className="group flex items-center gap-4 rounded-2xl border border-[#D5DEE8] bg-white p-4 transition-shadow hover:shadow-[var(--shadow-soft)]"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#7A1516]/10 text-[var(--az-icon-default)] transition-colors group-hover:text-[var(--az-icon-hover)] group-focus-visible:text-[var(--az-icon-hover)]">
                      <Phone size={18} />
                    </span>
                    <span>
                      <span className="block text-sm text-[#6e7683]">{h.label}</span>
                      <span className="font-semibold text-[#193D6D]">{h.number}</span>
                    </span>
                  </a>
                ))}

                <a
                  href={`mailto:${site.email}`}
                  className="group flex items-center gap-4 rounded-2xl border border-[#D5DEE8] bg-white p-4 transition-shadow hover:shadow-[var(--shadow-soft)]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#7A1516]/10 text-[var(--az-icon-default)] transition-colors group-hover:text-[var(--az-icon-hover)] group-focus-visible:text-[var(--az-icon-hover)]">
                    <Mail size={18} />
                  </span>
                  <span>
                    <span className="block text-sm text-[#6e7683]">{copy.details.emailLabel}</span>
                    <span className="font-semibold text-[#193D6D]">{site.email}</span>
                  </span>
                </a>

                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-address-maps-link"
                  className="flex items-start gap-4 rounded-2xl border border-[#D5DEE8] bg-white p-4 transition-shadow hover:shadow-[var(--shadow-soft)]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#7A1516]/10 text-[#7A1516]">
                    <MapPin size={18} />
                  </span>
                  <span>
                    <span className="block text-sm text-[#6e7683]">{copy.details.addressLabel}</span>
                    <span className="font-semibold text-[#193D6D]">
                      {site.address.line1}, {site.address.line2}
                    </span>
                    <span className="block text-sm text-[#6e7683]">{site.address.region}</span>
                  </span>
                </a>

                <div className="flex items-start gap-4 rounded-2xl border border-[#D5DEE8] bg-white p-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#7A1516]/10 text-[#7A1516]">
                    <Clock size={18} />
                  </span>
                  <span>
                    <span className="block text-sm text-[#6e7683]">{copy.details.hoursLabel}</span>
                    {openingHours.map((o) => (
                      <span key={o.days} className="block font-semibold text-[#193D6D]">
                        {o.days}: {o.time}
                      </span>
                    ))}
                  </span>
                </div>
              </div>

              {/* Social & quick-contact channels */}
              <div className="mt-6 rounded-2xl border border-[#D5DEE8] bg-[#E7EDF5] p-6" data-testid="contact-social-section">
                <h2 className="font-heading text-2xl font-semibold text-[#193D6D]">
                  {copy.social.title}
                </h2>
                <p className="mt-2 text-[#586576]">
                  {copy.social.subtitle}
                </p>
                <SocialBar size="lg" layout="contact" testidPrefix="contact-social" className="mt-5" />
              </div>
            </div>

            {/* Full form */}
            <div className="lg:col-span-7">
              <Reveal>
                <div
                  className="rounded-3xl border border-[#D5DEE8] bg-white p-6 shadow-[var(--shadow-card)] sm:p-9"
                  data-testid="contact-page-form"
                >
                  <h2 className="font-heading text-3xl font-semibold text-[#193D6D]">
                    {copy.form.title}
                  </h2>
                  <p className="mt-2 mb-6 text-[#586576]">
                    {copy.form.subtitle}
                  </p>
                  <ContactForm
                    sourcePage="kontakt"
                    testidPrefix="contact-page-form-fields"
                    submitLabel={copy.form.submitLabel}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
